/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CreateProjectForm } from './CreateProjectForm';
import SidebarContext from '~src/contexts/SidebarContext';
import { useStyles } from '~src/hooks/useStyles';

type DrawerState = 'closed' | 'menu' | 'form';

export const UserOptionsContainer: React.FC = () => {
  // Initialize state from sessionStorage: null or '0' = open ('menu'), '1' = closed
  const getInitialState = (): DrawerState => {
    const storedValue = window.sessionStorage.getItem(
      'is_user_options_dialog_closed'
    );
    if (storedValue === '1') {
      return 'closed';
    }
    // null or '0' defaults to open
    return 'menu';
  };

  const [state, setState] = useState<DrawerState>(getInitialState);
  const { isSidebarMinimized } = useContext(SidebarContext);
  const intl = useIntl();
  const styles = useStyles(styleFn);

  // Hide this panel entirely while the left sidebar is collapsed - it has
  // no room to render meaningfully at the minimized width, and should
  // reappear (in whatever open/closed state it was left in) once the
  // sidebar is expanded again.
  if (isSidebarMinimized) {
    return null;
  }

  const handleDrawerToggle = () => {
    const newState = state === 'closed' ? 'menu' : 'closed';
    setState(newState);
    // Persist to sessionStorage
    window.sessionStorage.setItem(
      'is_user_options_dialog_closed',
      newState === 'closed' ? '1' : '0'
    );
  };

  const handleOpenForm = () => {
    setState('form');
  };

  const handleCloseForm = () => {
    setState('menu');
  };

  const handleFullClose = () => {
    setState('closed');
    window.sessionStorage.setItem('is_user_options_dialog_closed', '1');
  };

  const getContainerStyle = () => {
    const base = styles.container;
    if (state === 'closed') {
      return { ...base, height: '47px', maxHeight: '47px', overflow: 'hidden' };
    }
    if (state === 'menu') {
      return {
        ...base,
        height: 'auto',
        maxHeight: '110px',
        overflow: 'hidden',
      };
    }
    return { ...base, height: 'auto', maxHeight: '70vh', overflow: 'hidden' };
  };

  const getToggleStyle = () => {
    const base = styles.drawerToggle;
    if (state !== 'closed') {
      return {
        ...base,
        color: '#FFFFFF',
        backgroundColor: '#ea8023',
        '&:hover': {
          backgroundColor: '#f8770c',
        },
        svg: {
          color: '#FFFFFF',
          position: 'absolute',
          right: '16px',
        },
      };
    }
    return base;
  };

  return (
    <div css={getContainerStyle()}>
      <div css={getToggleStyle()} onClick={handleDrawerToggle}>
        <span css={styles.toggleText}>
          {intl.formatMessage({ id: 'create-project-question' })}
        </span>
        <FontAwesomeIcon
          icon={['far', state === 'closed' ? 'chevron-up' : 'chevron-down']}
        />
      </div>

      {state === 'menu' && !isSidebarMinimized && (
        <div css={styles.optionsMenu}>
          <button onClick={handleOpenForm} css={styles.optionButton}>
            <FontAwesomeIcon icon={['far', 'folder-plus']} />
            <span css={styles.optionText}>
              {intl.formatMessage({ id: 'create-new-project' })}
            </span>
          </button>
        </div>
      )}

      {state === 'form' && !isSidebarMinimized && (
        <CreateProjectForm
          onClose={handleCloseForm}
          onSuccess={handleFullClose}
        />
      )}
    </div>
  );
};

const styleFn = (theme: any) => ({
  container: {
    position: 'sticky',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? theme.colors.primaryLight
        : window.sessionStorage.getItem('appState') === 'timeMachine'
        ? theme.colors.background.primaryLight
        : theme.colors.white,
    borderTop: `3px solid ${theme.colors.orange}`,
    boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
    zIndex: 10,
    transition: 'max-height 0.3s ease-in-out, height 0.3s ease-in-out',
  },
  drawerToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '48px',
    cursor: 'pointer',
    color: theme.colors.text.primary,
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    transition: 'background-color 0.2s, color 0.2s',
    padding: '0 16px',
    position: 'relative',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
    svg: {
      fontSize: '18px',
      transition: 'color 0.2s',
      color: theme.colors.text.primary,
      position: 'absolute',
      right: '16px',
    },
  },
  toggleText: {
    fontSize: '14px',
    fontWeight: 600,
  },
  optionsMenu: {
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  optionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    background: 'transparent',
    border: `1px solid ${theme.colors.darkGray}`,
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: theme.colors.text.primary,
    '&:hover': {
      backgroundColor: theme.colors.background.hover,
      borderColor: theme.colors.primary,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    svg: {
      fontSize: '18px',
      color: theme.colors.primary,
    },
  },
  optionText: {
    fontSize: '16px',
    fontWeight: 600,
    color: theme.colors.text.primary,
  },
});
